import { datadogRum } from '@datadog/browser-rum';
import {
	CardCvcElement,
	CardExpiryElement,
	CardNumberElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';
import { navigate } from 'gatsby';
import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import { CenteredLink } from '@ui/atoms/CenteredLink';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import SubmitButton from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';

import { ApiEndpoints } from '@utils/endpoints';
import { exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';

import { IMemberData } from '@folx-types/member';
import { IPaymentInfo } from '@folx-types/payment';

import theme from '@theme/FolxTheme';

import { IPaymentEditPageContent } from './PaymentEditStep.page';

interface IPaymentEditStepProps {
	paymentMethod?: IPaymentInfo;
	member?: IMemberData;
	refinedContent: IPaymentEditPageContent;
}

const InputStyle = css`
	font-family: 'Chalet', sans-serif;
	background-color: white;
	border: solid 0.1rem black;
	cursor: text;
	padding: ${theme.spacing.md} ${theme.spacing.sm} 2.2rem;
	width: 100%;
`;

const CardNumber = styled(CardNumberElement)`
	${InputStyle};
`;

const CardExpiry = styled(CardExpiryElement)`
	${InputStyle};
`;

const CardCvc = styled(CardCvcElement)`
	${InputStyle}
`;

const PageHeading = styled(HeadingText)`
	margin-bottom: ${theme.spacing.md};
`;

const Label = styled(BasicText).attrs({
	variant: 'field',
})`
	margin-bottom: ${theme.spacing.sm};
`;

const FullWidthColumn = fullWidth(FlexColumn);
const FullWidthSubmitButton = fullWidth(SubmitButton);

const PaymentEditStep: FC<IPaymentEditStepProps> = ({ paymentMethod, member, refinedContent }) => {
	const today = new Date();

	const stripe = useStripe();
	const elements = useElements();
	const [updatePaymentError, setUpdatePaymentError] = useState<string | undefined>('');
	const [submitting, setSubmitting] = useState<boolean>(false);

	const {
		butterPage: { title, info, cardNumberLabel, expDateLabel, cvvLabel, genericError },
	} = refinedContent;

	const savePaymentInfo = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!stripe || !elements || !member?.email) {
			return;
		}

		setSubmitting(true);

		const paymentMethod = await stripe.createPaymentMethod({
			type: 'card',
			card: elements.getElement(CardNumberElement) as StripeCardNumberElement,
			billing_details: {
				email: member?.email,
			},
		});

		if (paymentMethod.error) {
			datadogRum.addError(
				`stripe.createPaymentMethod: ${JSON.stringify({
					member: member?.id,
					response: paymentMethod,
				})}`
			);
			if (paymentMethod.error.message) {
				setUpdatePaymentError(paymentMethod.error.message);
			} else {
				setUpdatePaymentError('We were unable to verify your payment information.');
			}
			setSubmitting(false);
			return;
		}

		const authorizationToken = await getAuthToken();
		const response = await fetch(`${ApiEndpoints.stripe}/payment-method`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				authorizationToken,
			},
			body: JSON.stringify({
				paymentMethod: paymentMethod.paymentMethod,
			}),
		});

		if (!response.ok) {
			try {
				setUpdatePaymentError(((await response.json()) as { message?: string })?.message);
			} catch {
				setUpdatePaymentError('We were unable to update your payment information.');
			}
			setSubmitting(false);
			return;
		}

		setSubmitting(false);
		navigate('..');
	};

	return (
		<FullWidthColumn>
			<form id="payment" onSubmit={savePaymentInfo}>
				<PageHeading>{title}</PageHeading>
				<FlexColumn gap="lg">
					<BasicText>{info}</BasicText>
					<FullWidthColumn>
						<Label>{cardNumberLabel}</Label>
						<CardNumber
							id="cardNumber"
							options={{
								placeholder: `xxxx xxxx xxxx ${paymentMethod?.last4 ?? `xxxx`}`,
							}}
						/>
					</FullWidthColumn>
					<FullWidthColumn>
						<Label>{expDateLabel}</Label>
						<CardExpiry
							id="expDate"
							options={{
								placeholder: `${paymentMethod?.expMonth ?? today.getMonth() + 1}/${
									paymentMethod?.expYear ?? today.getFullYear().toString().substring(2)
								}`,
							}}
						/>
					</FullWidthColumn>
					<FullWidthColumn>
						<Label>{cvvLabel}</Label>
						<CardCvc
							id="cvv"
							options={{
								placeholder: `***`,
							}}
						/>
					</FullWidthColumn>
					<FullWidthSubmitButton
						label="Save"
						type="submit"
						variant="secondary"
						submitting={submitting}
					/>
					<CenteredLink variant="underline" to="..">
						Cancel
					</CenteredLink>
					{exists(updatePaymentError) && (
						<WYSIWYGContent
							color="red"
							tint="darken40"
							linkColor="black"
							dangerouslySetInnerHTML={{ __html: genericError }}
						/>
					)}
				</FlexColumn>
			</form>
		</FullWidthColumn>
	);
};

export default PaymentEditStep;
